import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Drawer,
  Box,
  Collapse,
  Stack,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
  Button,
  Alert,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Switch,
  FormControlLabel,
  alpha,
  darken,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth, usePermissions } from '../AuthContext';
import BrokerDrawer from './BrokerDrawer';
import {
  collection,
  doc,
  getFirestore,
  serverTimestamp,
  Timestamp,
  onSnapshot,
  orderBy,
  query,
  limit,
  writeBatch,
  getDocs,
  where,
} from 'firebase/firestore';

type LeadStatus = string;

type BrokerOption = {
  id: string;
  brokerName: string;
  agencyName: string | null;
  label: string;
};

function CardBlock({
  title,
  required,
  cardSx,
  labelSx,
  children,
}: {
  title: string;
  required?: boolean;
  cardSx: any;
  labelSx: any;
  children: React.ReactNode;
}) {
  return (
    <Box sx={cardSx}>
      <Typography variant="subtitle2" color="text.secondary" sx={labelSx}>
        {title}
        {required ? ' *' : ''}
      </Typography>
      {children}
    </Box>
  );
}

function buildPhoneSearchParts(phone10: string): string[] {
  const digits = String(phone10 || '').replace(/\D/g, '');
  if (digits.length !== 10) return [];
  const out = new Set<string>();
  // Substring search for phone: allow matching any contiguous digit sequence.
  for (let len = 3; len <= 10; len += 1) {
    for (let i = 0; i + len <= 10; i += 1) {
      out.add(digits.slice(i, i + len));
    }
  }
  return Array.from(out);
}

function buildTrigrams(value: string): string[] {
  const s = String(value || '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
  if (!s) return [];
  // 3-gram index for substring search. This stays small (O(n)).
  const out = new Set<string>();
  const padded = ` ${s} `;
  for (let i = 0; i + 3 <= padded.length; i += 1) {
    const gram = padded.slice(i, i + 3);
    // Skip trigrams that are only spaces.
    if (!gram.trim()) continue;
    out.add(gram);
  }
  return Array.from(out);
}

function parseLocalDate(dateStr: string): Date | null {
  const s = String(dateStr || '').trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d, 0, 0, 0, 0);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function parseLocalDateTime(dateStr: string, timeStr: string): Date | null {
  const base = parseLocalDate(dateStr);
  if (!base) return null;
  const t = String(timeStr || '').trim();
  const m = /^(\d{2}):(\d{2})$/.exec(t);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  base.setHours(hh, mm, 0, 0);
  return base;
}

type PromptType = 'text' | 'date' | 'number' | 'select';
type StatusPrompt = { id: string; label: string; type: PromptType; options?: string[] };

interface AddLeadDrawerProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (leadId?: string) => void;
  onLeadSelect?: (leadId: string) => void;
  prefill?: {
    phone?: string;
    name?: string;
    email?: string;
    source?: string;
    projectId?: string;
    notes?: string[];
    forceProjectSelection?: boolean;
    metaLeadQuickAdd?: boolean;
    lockedSource?: string;
  };
}

export default function AddLeadDrawer({ open, onClose, onCreated, onLeadSelect, prefill }: AddLeadDrawerProps) {
  const { currentUser, roleId, projectIds, currentProjectId } = useAuth();
  const { canAction } = usePermissions();
  const db = useMemo(() => getFirestore(), []);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [drawerTopOffset, setDrawerTopOffset] = useState(0);

  // form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [email, setEmail] = useState('');
  const [source, setSource] = useState('');
  const [referredBroker, setReferredBroker] = useState<BrokerOption | null>(null);
  const [c2cRefName, setC2cRefName] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [nextFollowUpTimeEnabled, setNextFollowUpTimeEnabled] = useState(false);
  const [nextFollowUpTime, setNextFollowUpTime] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [notes, setNotes] = useState<string[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [unitType, setUnitType] = useState<string>('');
  const [visitWith, setVisitWith] = useState<'' | 'alone' | 'family' | 'friend' | 'other'>('');
  const [visitWithOther, setVisitWithOther] = useState<string>('');
  const [visitBudgetFit, setVisitBudgetFit] = useState<string>('');
  const [visitFloorChoice, setVisitFloorChoice] = useState<string>('');
  const [visitUnitChoices, setVisitUnitChoices] = useState<string[]>([]);
  const [visitUnitChoiceInput, setVisitUnitChoiceInput] = useState<string>('');
  const [visitResidenceInput, setVisitResidenceInput] = useState<string>('');
  const [visitOccupationInput, setVisitOccupationInput] = useState<string>('');
  const [unitChoiceOptions, setUnitChoiceOptions] = useState<string[]>([]);

  // ui
  const [error, setError] = useState<string | null>(null);
  const [duplicateLead, setDuplicateLead] = useState<{ id: string; name: string } | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // statuses
  const [statuses, setStatuses] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const assignableUsers = useMemo(
    () => users.filter((user) => {
      const role = String(user.roleId || user.role || '').trim().toLowerCase();
      const displayName = String(user.name || '').trim().toLowerCase();
      // The shared "Account" service user is not a CRM lead owner, even if its
      // legacy user record carries a sales role from an earlier setup.
      if (displayName === 'account') return false;
      return ['admin', 'super-admin', 'sales-manager', 'salesmanager', 'sales-team', 'salesteam'].includes(role);
    }),
    [users]
  );
  const [followUpWorkload, setFollowUpWorkload] = useState<Record<string, { today: number; past: number }>>({});
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [workloadError, setWorkloadError] = useState(false);
  const [brokers, setBrokers] = useState<BrokerOption[]>([]);
  const [promptDraft, setPromptDraft] = useState<Record<string, string>>({});
  const [brokerDrawerOpen, setBrokerDrawerOpen] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);

  const prevOpenRef = useRef<boolean>(false);

  useEffect(() => {
    if (!open) {
      setDrawerTopOffset(0);
      return;
    }
    let raf = 0;
    const compute = () => {
      const el = document.querySelector<HTMLElement>('[data-role="app-header"]');
      if (!el) return 0;
      const rect = el.getBoundingClientRect();
      const visible = Math.max(0, Math.min(rect.height, rect.bottom));
      return Math.round(visible);
    };
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = compute();
        setDrawerTopOffset((prev) => (prev === next ? prev : next));
      });
    };
    schedule();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [open]);

  const visibleProjects = useMemo(() => {
    const r = (roleId || '').toString();
    const isAdmin = r === 'super-admin' || r === 'admin';
    if (isAdmin && (!projectIds || projectIds.length === 0)) return projects;
    const allowed = new Set((projectIds || []).filter(Boolean));
    return projects.filter((p) => allowed.has(p.id));
  }, [projectIds, projects, roleId]);
  const canInlineAddBroker = useMemo(
    () =>
      canAction('brokers', 'addBroker') ||
      canAction('leads', 'addBroker') ||
      String(roleId || '').trim().toLowerCase() === 'sales-manager' ||
      String(roleId || '').trim().toLowerCase() === 'salesmanager',
    [canAction, roleId]
  );

  const normalize = (v: string) => v.trim().toLowerCase().replace(/\s+/g, ' ');
  const normalizeNameLower = (v: string) => v.trim().replace(/\s+/g, ' ').toLowerCase();
  const hasSource = useMemo(() => !!source.trim(), [source]);
  const isWalkIn = useMemo(() => {
    const s = normalize(source);
    return s === 'walk in' || s === 'walk-in';
  }, [source]);
  const isWithBroker = useMemo(() => {
    const s = normalize(source);
    return s === 'with broker' || s === 'with brocker' || s === 'broker' || s === 'brocker';
  }, [source]);
  const isSocialAd = useMemo(() => {
    const s = normalize(source);
    return (
      s === 'social ad' ||
      s === 'social ads' ||
      s === 'social lead' ||
      s === 'meta lead' ||
      s === 'old lead' ||
      s === 'old leads' ||
      s === 'whatsapp' ||
      s === 'whats app' ||
      s === 'whatsapp lead' ||
      s === 'wa'
    );
  }, [source]);
  const forceProjectSelection = !!prefill?.forceProjectSelection;
  const isMetaLeadQuickAdd = !!prefill?.metaLeadQuickAdd;
  const lockedSource = String(prefill?.lockedSource || '').trim();
  const isCallSource = useMemo(() => {
    const s = normalize(source);
    return s === 'call' || s === 'incoming call';
  }, [source]);
  const isC2CSource = useMemo(() => {
    const s = normalize(source);
    return s === 'c2c';
  }, [source]);
  const showReferredBroker = useMemo(() => {
    const s = normalize(source);
    if (!s) return false;
    if (s === 'c2c') return false;
    return (
      s.includes('ref') ||
      s.includes('referred') ||
      s.includes('broker') ||
      s.includes('agency') ||
      s.includes('channel') ||
      s.includes('cp')
    );
  }, [source]);

  const selectedProject = useMemo(
    () => (projectId ? visibleProjects.find((p: any) => p.id === projectId) : null),
    [projectId, visibleProjects]
  );

  const unitTypeOptions = useMemo(() => {
    const raw = selectedProject?.unitTypes;
    return Array.isArray(raw) ? raw.filter((x: any) => typeof x === 'string' && x.trim()) : [];
  }, [selectedProject]);

  useEffect(() => {
    const statusesRef = collection(db, 'leadStatuses');
    const q = query(statusesRef, orderBy('order', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setStatuses(data);
    });
    return () => unsub();
  }, [db]);

  useEffect(() => {
    const q = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => setProjects(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))),
      () => setProjects([])
    );
    return () => unsub();
  }, [db]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [db]);

  useEffect(() => {
    if (!open) {
      setFollowUpWorkload({});
      setWorkloadLoading(false);
      setWorkloadError(false);
      return undefined;
    }
    // CRM follow-up day boundaries use India time, independent of browser TZ.
    const startInIST = new Date(Date.now() + 330 * 60000);
    startInIST.setUTCHours(0, 0, 0, 0);
    const startOfToday = new Date(startInIST.getTime() - 330 * 60000);
    const endOfToday = new Date(startOfToday.getTime() + 86400000);
    const endTs = Timestamp.fromDate(endOfToday);
    let dateRows: Array<{ id: string; data: any }> = [];
    let timeRows: Array<{ id: string; data: any }> = [];
    let dateLoaded = false;
    let timeLoaded = false;
    let active = true;
    setWorkloadLoading(true);
    setWorkloadError(false);

    const publish = () => {
      if (!active) return;
      const combined = new Map<string, any>();
      [...dateRows, ...timeRows].forEach((row) => combined.set(row.id, row.data));
      const counts: Record<string, { today: number; past: number }> = {};
      combined.forEach((lead) => {
        const rawStatus = String(lead.status || '').trim();
        const normalizedStatus = rawStatus.toLowerCase().replace(/\s+/g, ' ');
        const statusDoc = statuses.find((status) =>
          String(status?.id || '') === rawStatus ||
          String(status?.label || '').trim().toLowerCase().replace(/\s+/g, ' ') === normalizedStatus
        );
        const normalizedStatusLabel = String(statusDoc?.label || statusDoc?.id || normalizedStatus)
          .trim().toLowerCase().replace(/\s+/g, ' ');
        if (['closed', 'completed'].includes(normalizedStatusLabel)) return;

        const assignee = String(lead.assignedTo || '').trim().toLowerCase();
        if (!assignee) return;
        const due = lead.nextFollowUpAt?.toDate?.() || lead.nextFollowUpDate?.toDate?.();
        if (!(due instanceof Date) || Number.isNaN(due.getTime()) || due >= endOfToday) return;
        const bucket = due < startOfToday ? 'past' : 'today';
        counts[assignee] ||= { today: 0, past: 0 };
        counts[assignee][bucket] += 1;
      });
      setFollowUpWorkload(counts);
      if (dateLoaded && timeLoaded) setWorkloadLoading(false);
    };

    const onError = () => {
      if (!active) return;
      setWorkloadError(true);
      setWorkloadLoading(false);
    };
    const dateUnsubscribe = onSnapshot(
      query(collection(db, 'leads'), where('nextFollowUpDate', '<', endTs)),
      (snap) => {
        dateRows = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
        dateLoaded = true;
        publish();
      },
      onError
    );
    const timeUnsubscribe = onSnapshot(
      query(collection(db, 'leads'), where('nextFollowUpAt', '<', endTs)),
      (snap) => {
        timeRows = snap.docs.map((d) => ({ id: d.id, data: d.data() }));
        timeLoaded = true;
        publish();
      },
      onError
    );
    return () => {
      active = false;
      dateUnsubscribe();
      timeUnsubscribe();
    };
  }, [db, open, statuses]);

  useEffect(() => {
    const brokersRef = collection(db, 'brokers');
    const q = query(brokersRef, orderBy('displayNameLower', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      const data: BrokerOption[] = snap.docs.map((d) => {
        const v = d.data() as any;
        const brokerName = typeof v.brokerName === 'string' ? v.brokerName : '';
        const agencyName = typeof v.agencyName === 'string' ? v.agencyName : null;
        const label = `${agencyName ? `${agencyName} - ` : ''}${brokerName}`.trim();
        return { id: d.id, brokerName, agencyName, label };
      });
      setBrokers(data.filter((b) => b.label.length > 0));
    });
    return () => unsub();
  }, [db]);

  useEffect(() => {
    if (!open) return undefined;
    if (!projectId) {
      setUnitChoiceOptions([]);
      return undefined;
    }
    const ref = collection(db, 'projects', projectId, 'units');
    const q = query(ref, orderBy('unitNumberLower', 'asc'), limit(1000));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const vals = snap.docs
          .map((d) => (d.data() as any)?.unitNumber)
          .filter((v) => typeof v === 'string' && v.trim())
          .map((v) => String(v).trim());
        setUnitChoiceOptions(Array.from(new Set(vals)));
      },
      () => setUnitChoiceOptions([])
    );
    return () => unsub();
  }, [db, open, projectId]);



  const resetForm = useCallback(() => {
    setName('');
    setPhone('');
    setEmail('');
    setSource('');
    setReferredBroker(null);
    setC2cRefName('');
    setAssignedTo(currentUser?.email || '');
    // Default to the currently selected project (from header). If invalid, fall back to first allowed project.
    const valid =
      currentProjectId && visibleProjects.some((p: any) => p.id === currentProjectId)
        ? currentProjectId
        : (visibleProjects[0]?.id || '');
    setProjectId(valid);
    const p = visibleProjects.find((x: any) => x.id === valid);
    const uts = Array.isArray(p?.unitTypes) ? (p.unitTypes as any[]).filter((x) => typeof x === 'string' && x.trim()) : [];
    setUnitType(uts.length === 1 ? (uts[0] as string) : '');
    setNextFollowUpDate('');
    setNextFollowUpTimeEnabled(false);
    setNextFollowUpTime('');
    setNotes([]);
    setNoteInput('');
    setCountryCode('+91');
    setPromptDraft({});
    setVisitWith('');
    setVisitWithOther('');
    setVisitBudgetFit('');
    setVisitFloorChoice('');
    setVisitUnitChoices([]);
    setVisitUnitChoiceInput('');
    setVisitResidenceInput('');
    setVisitOccupationInput('');
    setError(null);
    setSuccess(null);
  }, [currentProjectId, currentUser?.email, visibleProjects]);

  useEffect(() => {
    if (open && !prevOpenRef.current) {
      resetForm();
      if (prefill) {
        if (prefill.phone) {
          const raw = prefill.phone.replace(/\D/g, '');
          if (raw.length > 10) {
            setCountryCode('+' + raw.slice(0, raw.length - 10));
            setPhone(raw.slice(-10));
          } else {
            setPhone(raw);
          }
        }
        if (prefill.name) setName(prefill.name);
        if (prefill.email) setEmail(prefill.email);
        if (prefill.lockedSource || prefill.source) setSource(prefill.lockedSource || prefill.source || '');
        if (prefill.projectId) setProjectId(prefill.projectId);
        if (prefill.notes?.length) setNotes(prefill.notes.filter((note) => typeof note === 'string' && note.trim()));
        if (String(prefill.source || '').trim().toLowerCase() === 'whatsapp') {
          const d = new Date();
          d.setHours(0, 0, 0, 0);
          setNextFollowUpDate(d.toISOString().slice(0, 10));
        }
      }
    }
    prevOpenRef.current = open;
  }, [open, resetForm, prefill]);

  useEffect(() => {
    if (!open) return;
    if (!isSocialAd || forceProjectSelection) return;
    // For Social Ad leads, follow-up defaults to today.
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const datePart = (type: string) => parts.find((part) => part.type === type)?.value || '';
    setNextFollowUpDate(`${datePart('year')}-${datePart('month')}-${datePart('day')}`);
  }, [forceProjectSelection, isSocialAd, open]);

  useEffect(() => {
    if (!open) return;
    if (isWalkIn || isWithBroker) {
      setNextFollowUpDate('');
      setNextFollowUpTimeEnabled(false);
      setNextFollowUpTime('');
    }
  }, [isWalkIn, isWithBroker, open]);

  useEffect(() => {
    if (!open) return;
    if (isWalkIn || isWithBroker) return;
    setVisitWith('');
    setVisitWithOther('');
    setVisitBudgetFit('');
    setVisitFloorChoice('');
    setVisitUnitChoices([]);
    setVisitUnitChoiceInput('');
    setVisitResidenceInput('');
    setVisitOccupationInput('');
  }, [isWalkIn, isWithBroker, open]);

  useEffect(() => {
    if (!open) return;
    if (!isSocialAd) return;
    // Social Ad: keep project/unitType unset; ask during Log Activity later if needed.
    if (projectId) setProjectId('');
    if (unitType) setUnitType('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceProjectSelection, isSocialAd, open]);

  useEffect(() => {
    if (!open) return;
    // If project changes, ensure the selected unit type is valid for the chosen project.
    if (unitTypeOptions.length === 0) return;
    if (unitType && unitTypeOptions.includes(unitType)) return;
    setUnitType(unitTypeOptions.length === 1 ? unitTypeOptions[0] : '');
  }, [open, projectId, unitType, unitTypeOptions]);

  const selectedStatusDoc = useMemo(() => {
    if (!statuses.length) return null;
    if (isWalkIn || isWithBroker) {
      const byId = statuses.find((s) => normalize(String(s.id || '')) === 'site-visited');
      if (byId) return byId;
      const byLabel = statuses.find((s) => normalize(String(s.label || '')) === 'site visited');
      if (byLabel) return byLabel;
      return statuses[0] || null;
    }
    // Default: New
    const byId = statuses.find((s) => normalize(String(s.id || '')) === 'new');
    if (byId) return byId;
    const byLabel = statuses.find((s) => normalize(String(s.label || '')).startsWith('new'));
    return byLabel || statuses[0] || null;
  }, [isWalkIn, isWithBroker, statuses]);

  const statusToSave: LeadStatus = useMemo(() => {
    if (selectedStatusDoc?.id) return selectedStatusDoc.id;
    return isWalkIn || isWithBroker ? 'site-visited' : 'new';
  }, [isWalkIn, isWithBroker, selectedStatusDoc?.id]);

  const selectedStatusPrompts: StatusPrompt[] = useMemo(() => {
    const p = (selectedStatusDoc?.prompts || []) as StatusPrompt[];
    return Array.isArray(p) ? p : [];
  }, [selectedStatusDoc]);

  useEffect(() => {
    // Prompts shown depend on the auto-selected status; avoid mixing drafts across statuses.
    setPromptDraft({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatusDoc?.id]);

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setDuplicateLead(null);
    setSuccess(null);
    if (!source.trim()) {
      setError('Source is required.');
      return;
    }
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    if (isC2CSource && !c2cRefName.trim()) {
      setError('C2C name is required.');
      return;
    }
    if ((!isSocialAd || forceProjectSelection) && !projectId) {
      setError('Project is required.');
      return;
    }
    if ((!isSocialAd || forceProjectSelection) && (!isWalkIn || !!projectId)) {
      if (unitTypeOptions.length > 0) {
        if (!unitType) {
          setError('Unit Type is required.');
          return;
        }
      } else {
        // No predefined unit types in this project - still require a manual value.
        if (!unitType.trim()) {
          setError('Unit Type is required.');
          return;
        }
      }
    }
    const normalizedPhone = phone.replace(/[^0-9]/g, '');
    const isNoPhoneToken = normalizedPhone === '00';
    if (!isNoPhoneToken && normalizedPhone.length !== 10) {
      setError('Phone must be exactly 10 digits, or use 00 for no-phone walk-in.');
      return;
    }
    const fullPhone = isNoPhoneToken ? '00' : `${countryCode}${normalizedPhone}`;
    if (!isNoPhoneToken) {
      setSaving(true);
      try {
        const q = query(
          collection(db, 'leads'),
          where('phone', '==', fullPhone),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const d = snap.docs[0];
          const existing = { id: d.id, name: String(d.data()?.name || '').trim() };
          setDuplicateLead(existing);
          setError(`This phone number already exists — ${existing.name || 'Unknown'}`);
          setSaving(false);
          return;
        }
      } catch (err) {
        console.error('Error checking duplicate phone:', err);
        setError('Failed to verify phone uniqueness.');
        setSaving(false);
        return;
      }
      setSaving(false);
    }

    // Prompts are optional; validate only if user entered a value.
    for (const p of selectedStatusPrompts) {
      const raw = (promptDraft[p.id] || '').trim();
      if (!raw) continue;
      if (p.type === 'number' && Number.isNaN(Number(raw))) {
        setError(`Invalid number for: ${p.label}`);
        return;
      }
      if (p.type === 'date' && Number.isNaN(new Date(raw).getTime())) {
        setError(`Invalid date for: ${p.label}`);
        return;
      }
    }

    if ((isWalkIn || isWithBroker) && !visitWith) {
      setError('Select Visit With.');
      return;
    }
    if ((isWalkIn || isWithBroker) && visitWith === 'other' && !visitWithOther.trim()) {
      setError('Enter Visit With (Other).');
      return;
    }
    if (!nextFollowUpDate) {
      setError('Select Next Follow-up date.');
      return;
    }
    if (!parseLocalDate(nextFollowUpDate)) {
      setError('Invalid Next Follow-up date.');
      return;
    }

    setSaving(true);
    try {
      const promptAnswers: Record<string, any> = {};
      const activityPromptValues: Record<string, any> = {};
      if (!isCallSource && selectedStatusDoc && selectedStatusPrompts.length > 0) {
        selectedStatusPrompts.forEach((p) => {
          const raw = (promptDraft[p.id] || '').trim();
          if (!raw) return;
          const value =
            p.type === 'number' ? Number(raw) :
            p.type === 'date' ? Timestamp.fromDate(new Date(raw)) :
            raw;
          promptAnswers[p.id] = {
            value,
            type: p.type,
            label: p.label,
            statusId: selectedStatusDoc.id,
            answeredAt: serverTimestamp(),
            answeredBy: currentUser?.email || null,
          };
          activityPromptValues[p.id] = value;
        });
      }

      const projectToSave = isSocialAd && !forceProjectSelection ? null : (projectId || null);
      const projectNameToSave = isSocialAd && !forceProjectSelection ? null : (selectedProject?.name || null);
      const unitTypeToSave = isSocialAd && !forceProjectSelection ? null : (unitType.trim() || null);
      const referredBrokerIdToSave = isCallSource ? null : (referredBroker?.id || null);
      const referredBrokerNameToSave = isCallSource ? null : (referredBroker?.brokerName || null);
      const referredAgencyNameToSave = isCallSource ? null : (referredBroker?.agencyName || null);
      const refByNameToSave =
        isCallSource ? null : (isC2CSource ? (c2cRefName.trim() || null) : null);
      const refByTypeToSave =
        isCallSource ? null : (isC2CSource && c2cRefName.trim() ? 'c2c' : null);
      const emailToSave = isCallSource ? null : (email.trim() || null);
      const notesToSave = notes;
      const followUpToSave = (() => {
        const dt = parseLocalDate(nextFollowUpDate);
        return dt ? Timestamp.fromDate(dt) : null;
      })();
      const followUpAtToSave =
        nextFollowUpDate && nextFollowUpTimeEnabled && nextFollowUpTime
          ? (() => {
              const dt = parseLocalDateTime(nextFollowUpDate, nextFollowUpTime);
              return dt ? Timestamp.fromDate(dt) : null;
            })()
          : null;

      const isInitialVisit = isWalkIn || isWithBroker;
      const visitDefs: Record<string, { id: string; label: string; type: 'text' | 'select' }> = {
        legacy_budget: { id: 'legacy_budget', label: 'Budget Fit', type: 'select' },
        legacy_floorChoice: { id: 'legacy_floorChoice', label: 'Floor Choice', type: 'select' },
        legacy_unitChoice: { id: 'legacy_unitChoice', label: 'Unit Choice', type: 'text' },
        legacy_currentResidence: { id: 'legacy_currentResidence', label: 'Current Residence', type: 'text' },
        legacy_occupation: { id: 'legacy_occupation', label: 'Occupation', type: 'text' },
      };
      const maybeAddVisitPrompt = (id: keyof typeof visitDefs, value: any) => {
        const v = value;
        if (v === null || v === undefined) return;
        if (typeof v === 'string' && !v.trim()) return;
        if (Array.isArray(v) && v.length === 0) return;
        const def = visitDefs[id];
        promptAnswers[id] = {
          value: Array.isArray(v)
            ? v.filter((x) => typeof x === 'string' && x.trim()).map((x) => x.trim())
            : String(v).trim(),
          type: def.type,
          label: def.label,
          statusId: statusToSave,
          answeredAt: serverTimestamp(),
          answeredBy: currentUser?.email || null,
        };
        activityPromptValues[id] = promptAnswers[id].value;
      };

      if (isInitialVisit) {
        maybeAddVisitPrompt('legacy_budget', visitBudgetFit);
        maybeAddVisitPrompt('legacy_floorChoice', visitFloorChoice);
        maybeAddVisitPrompt('legacy_unitChoice', visitUnitChoices);
        maybeAddVisitPrompt('legacy_currentResidence', visitResidenceInput);
        maybeAddVisitPrompt('legacy_occupation', visitOccupationInput);
      }

      const leadRef = doc(collection(db, 'leads'));
      const batch = writeBatch(db);
      batch.set(leadRef, {
        name: name.trim(),
        nameLower: normalizeNameLower(name),
        nameTrigrams: buildTrigrams(normalizeNameLower(name)),
        projectId: projectToSave,
        projectName: projectNameToSave,
        unitType: unitTypeToSave,
        phone: fullPhone,
        phone10: isNoPhoneToken ? '00' : normalizedPhone,
        phoneSearch: isNoPhoneToken ? [] : buildPhoneSearchParts(normalizedPhone),
        email: emailToSave,
        emailLower: emailToSave ? emailToSave.toLowerCase() : null,
        source: source.trim() || null,
        sourceLower: source.trim() ? source.trim().toLowerCase() : null,
        referredBrokerId: referredBrokerIdToSave,
        referredBrokerName: referredBrokerNameToSave,
        referredAgencyName: referredAgencyNameToSave,
        referredBrokerAt: referredBrokerIdToSave ? serverTimestamp() : null,
        refByName: refByNameToSave,
        refByType: refByTypeToSave,
        refByAt: refByNameToSave ? serverTimestamp() : null,
        status: statusToSave,
        assignedTo: assignedTo.trim() || currentUser?.email || null,
        assignedAt: assignedTo.trim() || currentUser?.email ? serverTimestamp() : null,
        assignedToName: null,
        nextFollowUpDate: followUpToSave,
        nextFollowUpAt: followUpAtToSave,
        notes: notesToSave,
        // Used by follow-up quick filters ("No Activity") and timeline ordering helpers.
        activityCount: isInitialVisit ? 1 : 0,
        // Denormalized visit count for fast list rendering.
        visitCount: isInitialVisit ? 1 : 0,
        lastActivityAt: isInitialVisit ? serverTimestamp() : null,
        promptAnswers: Object.keys(promptAnswers).length ? promptAnswers : {},
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser?.uid || null,
      });

      if (isInitialVisit) {
        const activitiesRef = collection(leadRef, 'activities');
        const activityRef = doc(activitiesRef);
        batch.set(activityRef, {
          type: 'visit',
          callOutcome: null,
          visitWith: visitWith || null,
          visitWithOther: visitWith === 'other' ? (visitWithOther.trim() || null) : null,
          notes: notes.length ? notes : null,
          promptAnswers: Object.keys(activityPromptValues).length ? activityPromptValues : null,
          statusFrom: null,
          statusTo: statusToSave || null,
          followUpFrom: null,
          followUpDate: followUpToSave,
          followUpAt: followUpAtToSave,
          byEmail: currentUser?.email || null,
          at: serverTimestamp(),
        });
      }

      await batch.commit();
      setSuccess('Lead created.');
      resetForm();
      onCreated?.(leadRef.id);
      onClose();
    } catch (err: any) {
      console.error('Create lead error', err);
      setError(err.message || 'Failed to create lead.');
    } finally {
      setSaving(false);
    }
  };

  // Match the lighter "cards" UI used in Log Activity.
  const cardsUi = useMemo(
    () => ({
      cardSx: {
        p: 1.75,
        borderRadius: 3,
        bgcolor: 'rgba(15, 23, 42, 0.04)',
        border: '1px solid',
        borderColor: 'rgba(15, 23, 42, 0.08)',
      },
      labelSx: {
        mb: 1,
        fontWeight: 800,
        letterSpacing: 0.2,
      },
      inputSx: {
        '& .MuiOutlinedInput-root': {
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          borderRadius: 2,
        },
      },
      pillUnselectedSx: {
        borderRadius: 999,
        px: 1.25,
        py: 2.25,
        fontWeight: 700,
        borderWidth: 1,
        borderStyle: 'solid',
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(15, 23, 42, 0.14)',
        color: 'text.primary',
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
      },
      pillSelectedSx: {
        borderRadius: 999,
        px: 1.25,
        py: 2.25,
        fontWeight: 800,
        borderWidth: 1,
        borderStyle: 'solid',
        bgcolor: alpha(theme.palette.primary.main, 0.14),
        borderColor: alpha(theme.palette.primary.main, 0.34),
        color: theme.palette.primary.dark,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          borderColor: alpha(theme.palette.primary.main, 0.42),
        },
      },
      primaryBtnSx: {
        fontWeight: 800,
        boxShadow: 'none',
        '&:hover': { boxShadow: 'none', bgcolor: darken(theme.palette.primary.main, 0.12) },
      },
      pillCompactUnselectedSx: {
        borderRadius: 999,
        px: 0.75,
        py: 0.8,
        fontWeight: 700,
        fontSize: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        borderColor: 'rgba(15, 23, 42, 0.14)',
        color: 'text.primary',
        minHeight: 30,
        '&:hover': { bgcolor: 'rgba(255, 255, 255, 1)' },
      },
      pillCompactSelectedSx: {
        borderRadius: 999,
        px: 0.75,
        py: 0.8,
        fontWeight: 800,
        fontSize: 12,
        minHeight: 30,
        borderWidth: 1,
        borderStyle: 'solid',
        bgcolor: alpha(theme.palette.primary.main, 0.14),
        borderColor: alpha(theme.palette.primary.main, 0.34),
        color: theme.palette.primary.dark,
        '&:hover': {
          bgcolor: alpha(theme.palette.primary.main, 0.18),
          borderColor: alpha(theme.palette.primary.main, 0.42),
        },
      },
    }),
    [theme.palette.primary.dark, theme.palette.primary.main]
  );

  const userLabel = (u: any) => {
    const name = typeof u?.name === 'string' ? u.name.trim() : '';
    if (name) return name;
    const email = typeof u?.email === 'string' ? u.email : '';
    if (email && email.includes('@')) return email.split('@')[0];
    return email || '—';
  };

  const dateToISO = (d: Date) => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.toISOString().slice(0, 10);
  };

  const quickFollowUps = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const addDays = (n: number) => {
      const d = new Date(today);
      d.setDate(d.getDate() + n);
      return dateToISO(d);
    };
    const nextDayOfWeekISO = (targetDow: number) => {
      const d = new Date(today);
      const dow = d.getDay(); // 0=Sun ... 6=Sat
      let diff = (targetDow - dow + 7) % 7;
      if (diff === 0) diff = 7; // "next" (not today)
      d.setDate(d.getDate() + diff);
      return dateToISO(d);
    };
    return [
      { key: 'today', label: 'Today', iso: addDays(0) },
      { key: 'tomorrow', label: 'Tomorrow', iso: addDays(1) },
      { key: 'd3', label: '+3 days', iso: addDays(3) },
      { key: 'd7', label: '+7 days', iso: addDays(7) },
      { key: 'sat', label: 'Next Saturday', iso: nextDayOfWeekISO(6) },
      { key: 'sun', label: 'Next Sunday', iso: nextDayOfWeekISO(0) },
    ];
  }, []);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Prevent mount/unmount churn (and focus loss) on some mobile browsers.
      ModalProps={{ keepMounted: true }}
      PaperProps={{
        sx: {
          width: isMobile ? '100%' : 420,
          maxWidth: '100%',
          top: `${drawerTopOffset}px`,
          height: `calc(100% - ${drawerTopOffset}px)`,
        },
      }}
    >
      <Box sx={{ width: isMobile ? '100%' : 420, maxWidth: '100%', p: { xs: 1.5, sm: 3 } }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Add Lead</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
        <Box component="form" onSubmit={(e) => e.preventDefault()} noValidate>
          <Stack spacing={1.25}>
            {!lockedSource && <CardBlock title="Source" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                  gap: 1,
                }}
              >
                {[
                  { key: 'walkin', label: 'Walk In', value: 'Walk In' },
                  { key: 'broker', label: 'With Broker', value: 'With Broker' },
                  { key: 'whatsapp', label: 'WhatsApp', value: 'WhatsApp' },
                  { key: 'social', label: 'Social Ad', value: 'Social Ad' },
                  { key: 'old', label: 'Old Lead', value: 'Old Lead' },
                  { key: 'call', label: 'Call', value: 'Call' },
                  { key: 'c2c', label: 'C2C', value: 'C2C' },
                ].map((opt) => {
                  const selected = normalize(source) === normalize(opt.value);
                  return (
                    <Button
                      key={opt.key}
                      variant="outlined"
                      fullWidth
                      onClick={() => setSource(opt.value)}
                      sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                    >
                      {opt.label}
                    </Button>
                  );
                })}
              </Box>
              {!hasSource && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Select Source first to continue.
                </Typography>
              )}
            </CardBlock>}

            <Collapse in={hasSource} unmountOnExit>
              <Stack spacing={1.25}>
                {(!isSocialAd || forceProjectSelection) && (
                  <CardBlock title="Project" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    {visibleProjects.length === 0 ? (
                      <Alert severity="warning">No projects available</Alert>
                    ) : (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {visibleProjects
                          .slice()
                          .sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || '')))
                          .map((p: any) => {
                            const selected = projectId === p.id;
                            return (
                              <Button
                                key={p.id}
                                onClick={() => {
                                  setProjectId(p.id);
                                  setUnitType('');
                                }}
                                variant="outlined"
                                fullWidth
                                sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                              >
                                {p.name}
                              </Button>
                            );
                          })}
                      </Box>
                    )}
                  </CardBlock>
                )}

                {(!isSocialAd || forceProjectSelection) && (!isWalkIn || !!projectId) && (
                  <CardBlock title="Unit Type" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    {unitTypeOptions.length > 0 ? (
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                          gap: 1,
                        }}
                      >
                        {unitTypeOptions.map((u) => {
                          const selected = unitType === u;
                          return (
                            <Button
                              key={u}
                              onClick={() => setUnitType(u)}
                              variant="outlined"
                              fullWidth
                              sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                            >
                              {u}
                            </Button>
                          );
                        })}
                      </Box>
                    ) : (
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="e.g., 3BHK"
                        value={unitType}
                        onChange={(e) => setUnitType(e.target.value)}
                        sx={cardsUi.inputSx}
                      />
                    )}
                  </CardBlock>
                )}

                <CardBlock title="Name" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    sx={cardsUi.inputSx}
                  />
                </CardBlock>

                <CardBlock title="Mobile" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl sx={{ minWidth: 90 }}>
                      <Select
                        size="small"
                        value={countryCode}
                        onChange={(e) => setCountryCode(e.target.value as string)}
                        sx={cardsUi.inputSx}
                      >
                        <MenuItem value="+91">+91</MenuItem>
                        <MenuItem value="+1">+1</MenuItem>
                        <MenuItem value="+44">+44</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="10 digit mobile"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                      sx={cardsUi.inputSx}
                    />
                  </Stack>
                </CardBlock>

                {(!isSocialAd || lockedSource) && !isCallSource && !isMetaLeadQuickAdd && (
                  <CardBlock title="Email" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      sx={cardsUi.inputSx}
                    />
                  </CardBlock>
                )}

                {!isSocialAd && !isCallSource && showReferredBroker && (
                  <CardBlock title="Referred By (Broker)" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Autocomplete
                          options={brokers}
                          value={referredBroker}
                          onChange={(_, v) => setReferredBroker(v)}
                          getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          renderInput={(params) => (
                            <TextField {...params} size="small" placeholder="Search broker…" sx={cardsUi.inputSx} />
                          )}
                        />
                      </Box>
                      {canInlineAddBroker && (
                        <Button
                          variant="outlined"
                          onClick={() => setBrokerDrawerOpen(true)}
                          sx={cardsUi.pillCompactUnselectedSx}
                        >
                          +
                        </Button>
                      )}
                    </Stack>
                  </CardBlock>
                )}

                {!isSocialAd && !isCallSource && isC2CSource && (
                  <CardBlock title="C2C Ref By" required cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Enter C2C name"
                      value={c2cRefName}
                      onChange={(e) => setC2cRefName(e.target.value)}
                      sx={cardsUi.inputSx}
                    />
                  </CardBlock>
                )}

                {!isSocialAd && !isCallSource && (
                  <CardBlock title="Status" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <TextField
                      fullWidth
                      size="small"
                      value={selectedStatusDoc?.label || (isWalkIn || isWithBroker ? 'Site Visited' : 'New')}
                      disabled
                      sx={cardsUi.inputSx}
                    />
                  </CardBlock>
                )}

                {!isSocialAd && !isCallSource && selectedStatusPrompts.length > 0 && (
                  <CardBlock title="Additional Details" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <Stack spacing={1}>
                      {selectedStatusPrompts.map((p) => (
                        <TextField
                          key={p.id}
                          fullWidth
                          size="small"
                          placeholder={p.label}
                          type={p.type === 'number' ? 'number' : p.type === 'date' ? 'date' : 'text'}
                          InputLabelProps={p.type === 'date' ? { shrink: true } : undefined}
                          value={promptDraft[p.id] || ''}
                          onChange={(e) => setPromptDraft((prev) => ({ ...prev, [p.id]: e.target.value }))}
                          select={p.type === 'select'}
                          sx={cardsUi.inputSx}
                        >
                          {p.type === 'select' &&
                            (p.options || []).map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                        </TextField>
                      ))}
                    </Stack>
                  </CardBlock>
                )}

                {(isWalkIn || isWithBroker) && (
                  <CardBlock title="Site Visit Details" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <Stack spacing={1.25}>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Visit With *
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {[
                            { key: 'alone', label: 'Alone' },
                            { key: 'family', label: 'With Family' },
                            { key: 'friend', label: 'With Relative' },
                            { key: 'other', label: 'Other' },
                          ].map((o) => {
                            const selected = visitWith === (o.key as any);
                            return (
                              <Button
                                key={o.key}
                                variant="outlined"
                                onClick={() => setVisitWith(o.key as any)}
                                sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                              >
                                {o.label}
                              </Button>
                            );
                          })}
                        </Box>
                        {visitWith === 'other' && (
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Other (Visit With)"
                            value={visitWithOther}
                            onChange={(e) => setVisitWithOther(e.target.value)}
                            sx={{ ...cardsUi.inputSx, mt: 1 }}
                          />
                        )}
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Budget Fit
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {['In Budget', 'Under Budget'].map((opt) => {
                            const selected = visitBudgetFit === opt;
                            return (
                              <Button
                                key={opt}
                                variant="outlined"
                                onClick={() => setVisitBudgetFit(opt)}
                                sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                              >
                                {opt}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
                          Floor Choice
                        </Typography>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: 1,
                          }}
                        >
                          {['Lower', 'Middle', 'Higher', 'Any'].map((opt) => {
                            const selected = visitFloorChoice === opt;
                            return (
                              <Button
                                key={opt}
                                variant="outlined"
                                onClick={() => setVisitFloorChoice(opt)}
                                sx={selected ? cardsUi.pillCompactSelectedSx : cardsUi.pillCompactUnselectedSx}
                              >
                                {opt}
                              </Button>
                            );
                          })}
                        </Box>
                      </Box>

	                      <Box>
	                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.75, fontWeight: 800 }}>
	                          Unit Choice
	                        </Typography>
		                        <Autocomplete
		                          multiple
		                          options={unitChoiceOptions}
		                          value={visitUnitChoices}
		                          inputValue={visitUnitChoiceInput}
		                          onInputChange={(_, val) => setVisitUnitChoiceInput(val)}
		                          onChange={(_, val) =>
		                            setVisitUnitChoices(
		                              (val || [])
		                                .filter((x) => typeof x === 'string' && x.trim())
		                                .map((x) => x.trim())
		                            )
		                          }
		                          renderTags={(value, getTagProps) =>
		                            (value || []).map((option, index) => (
		                              <Chip
		                                // eslint-disable-next-line react/jsx-key
		                                {...getTagProps({ index })}
		                                label={option}
		                                variant="filled"
		                                size="small"
		                                sx={{
		                                  ...cardsUi.pillCompactSelectedSx,
                                  py: 0.15,
                                  '& .MuiChip-label': { px: 0.75 },
		                                }}
		                              />
		                            ))
		                          }
		                          renderInput={(params) => (
		                            <TextField
		                              {...params}
		                              size="small"
	                              placeholder={
	                                unitChoiceOptions.length
	                                  ? 'Select unit number(s)'
	                                  : 'No units uploaded for this project'
	                              }
	                              sx={cardsUi.inputSx}
	                            />
	                          )}
	                        />
	                      </Box>

                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Current Residence"
                        value={visitResidenceInput}
                        onChange={(e) => setVisitResidenceInput(e.target.value)}
                        sx={cardsUi.inputSx}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Occupation"
                        value={visitOccupationInput}
                        onChange={(e) => setVisitOccupationInput(e.target.value)}
                        sx={cardsUi.inputSx}
                      />
                    </Stack>
                  </CardBlock>
                )}

                <CardBlock title="Assigned To" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                  <FormControl fullWidth>
                    <Select
                      size="small"
                      value={assignedTo}
                      onChange={(e) => setAssignedTo(e.target.value)}
                      displayEmpty
                      renderValue={(value) => {
                        const selected = assignableUsers.find((user) => user.email === value);
                        return selected ? userLabel(selected) : '';
                      }}
                      sx={cardsUi.inputSx}
                    >
                      {assignableUsers.map((u) => {
                        const role = String(u.roleId || u.role || '').trim().toLowerCase();
                        const isAdminUser = role === 'admin' || role === 'super-admin';
                        const emailKey = String(u.email || '').trim().toLowerCase();
                        const workload = followUpWorkload[emailKey] || { today: 0, past: 0 };
                        return (
                          <MenuItem key={u.id} value={u.email} sx={{ minWidth: 250, gap: 2, justifyContent: 'space-between' }}>
                            <Typography component="span" sx={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {userLabel(u)}
                            </Typography>
                            {!isAdminUser && (
                              <Typography component="span" sx={{ color: 'text.secondary', fontSize: 11.5, whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {workloadLoading ? 'Loading…' : workloadError ? 'Counts unavailable' : `Today ${workload.today} · Past ${workload.past}`}
                              </Typography>
                            )}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </CardBlock>

                {(!isSocialAd || lockedSource) && !isMetaLeadQuickAdd && (
                  <CardBlock title="Next Follow-up" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <TextField
                      type="date"
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                      value={nextFollowUpDate}
                      onChange={(e) => setNextFollowUpDate(e.target.value)}
                      sx={cardsUi.inputSx}
                    />
                    <FormControlLabel
                      sx={{
                        mt: 1,
                        ml: 0,
                        mr: 0,
                        '& .MuiFormControlLabel-label': { fontWeight: 600 },
                      }}
                      control={(
                        <Switch
                          size="small"
                          checked={nextFollowUpTimeEnabled}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setNextFollowUpTimeEnabled(checked);
                            if (checked) {
                              const now = new Date();
                              const hh = String(now.getHours()).padStart(2, '0');
                              const mm = String(now.getMinutes()).padStart(2, '0');
                              setNextFollowUpTime((prev) => prev || `${hh}:${mm}`);
                            } else {
                              setNextFollowUpTime('');
                            }
                          }}
                        />
                      )}
                      label="Set reminder time"
                    />
                    {nextFollowUpTimeEnabled && (
                      <TextField
                        type="time"
                        fullWidth
                        size="small"
                        value={nextFollowUpTime}
                        onChange={(e) => setNextFollowUpTime(e.target.value)}
                        sx={{ ...cardsUi.inputSx, mt: 1.25 }}
                        inputProps={{ step: 300 }}
                      />
                    )}
                    <Box
                      sx={{
                        mt: 1,
                        display: 'grid',
                        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
                        gap: 0.75,
                      }}
                    >
                      {quickFollowUps.map((qf) => {
                        const selected = nextFollowUpDate === qf.iso;
                        const pillSx = selected
                          ? {
                              ...cardsUi.pillCompactSelectedSx,
                              px: 0.75,
                              py: 0.5,
                              fontSize: 11,
                              minHeight: 30,
                            }
                          : {
                              ...cardsUi.pillCompactUnselectedSx,
                              px: 0.75,
                              py: 0.5,
                              fontSize: 11,
                              minHeight: 30,
                            };
                        return (
                          <Button
                            key={qf.key}
                            variant="outlined"
                            fullWidth
                            onClick={() => setNextFollowUpDate(qf.iso)}
                            sx={pillSx}
                          >
                            {qf.label}
                          </Button>
                        );
                      })}
                    </Box>
                  </CardBlock>
                )}

                {(!isSocialAd || lockedSource) && (
                  <CardBlock title="Notes" cardSx={cardsUi.cardSx} labelSx={cardsUi.labelSx}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Type note and press Enter…"
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const t = noteInput.trim();
                          if (!t) return;
                          setNotes((prev) => [...prev, t]);
                          setNoteInput('');
                        }
                      }}
                      sx={cardsUi.inputSx}
                    />
                    {notes.length > 0 && (
                      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
                        {notes.map((n, idx) => (
                          <Chip
                            key={`${idx}-${n}`}
                            label={n}
                            onDelete={() => setNotes((prev) => prev.filter((_, i) => i !== idx))}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255, 255, 255, 0.92)',
                              border: '1px solid',
                              borderColor: 'rgba(15, 23, 42, 0.14)',
                              borderRadius: 999,
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </CardBlock>
                )}
              </Stack>
            </Collapse>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
              {duplicateLead && onLeadSelect && (
                <Box
                  component="span"
                  sx={{ ml: 1, cursor: 'pointer', textDecoration: 'underline', fontWeight: 600, color: 'inherit' }}
                  onClick={() => { onClose(); onLeadSelect(duplicateLead.id); }}
                >
                  View: {duplicateLead.name || 'Open Lead'}
                </Box>
              )}
            </Alert>
          )}
          {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}

          <Button
            type="button"
            fullWidth
            variant="contained"
            sx={{ mt: 3 }}
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!canAction('leads','addLead') || saving || !hasSource}
          >
            {canAction('leads','addLead') ? (saving ? 'Saving...' : 'Add Lead') : 'No permission'}
          </Button>
        </Box>
      </Box>
      <BrokerDrawer
        open={brokerDrawerOpen}
        onClose={() => setBrokerDrawerOpen(false)}
        onSaved={(saved) => {
          setBrokerDrawerOpen(false);
          if (!saved) return;
          setReferredBroker({
            id: saved.id,
            brokerName: saved.brokerName,
            agencyName: saved.agencyName,
            label: `${saved.agencyName ? `${saved.agencyName} - ` : ''}${saved.brokerName}`.trim(),
          });
        }}
        editing={null}
      />
    </Drawer>
  );
}
